// useFilters.ts
import { useState, useMemo, useContext, useCallback } from 'react';
import {
  flattenCarlineGroups,
  filterCarlineGroupsByVehicleType,
  filterByCarlineGroup,
  filterByBodyType,
  vehicleTypes,
  insertNewCarlineGroup,
  uniqueByFirst,
} from '../utils/filterUtils';
import {
  getBodyTypePreset,
  getCarlineGroupPreset,
  getVehicleTypePreset,
} from '../utils/urlPresets';
import { DataContext } from '../context/DataContext';

import type { Carline, DisplayedVehicleType } from '../utils/filterUtils';
import type { CarlineGroup } from '../../@types/mapped';

export type ActiveFilter =
  | { id: string; type: 'bodyType' }
  | { id: string; type: 'carlineGroup' }
  | { id: DisplayedVehicleType; type: 'vehicleType' };
interface UseFiltersProps {
  preFilteredCarlineGroup?: string;
}
export interface FilterState {
  selectedBodyTypes: string[];
  setSelectedBodyTypes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCarlineGroups: string[];
  setSelectedCarlineGroups: React.Dispatch<React.SetStateAction<string[]>>;
  selectedVehicleType: DisplayedVehicleType[];
  setSelectedVehicleType: React.Dispatch<React.SetStateAction<DisplayedVehicleType[]>>;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  activeFilters: ActiveFilter[];
  removeActiveFilter: (f: ActiveFilter) => void;
  preFilteredCarlineGroups: CarlineGroup[];
  flattenedCarlines: Carline[];
  carlinesFilteredByCarlineGroup: Carline[];
  carlinesFilteredByBodyType: Carline[];
  filteredCarlines: Carline[];
  carlineGroupsForTags: CarlineGroup[];
  availableVehicleTypes: DisplayedVehicleType[];
  resetAllFilters: () => void;
  bodyOrModelFilterActive: boolean;
}

export const useFilters = ({ preFilteredCarlineGroup }: UseFiltersProps = {}): FilterState => {
  const { allCarlineGroups } = useContext(DataContext);

  const [selectedCarlineGroups, setSelectedCarlineGroups] = useState<string[]>(() => {
    const preset = getCarlineGroupPreset(preFilteredCarlineGroup ?? '');
    return preset;
  });

  const [selectedBodyTypes, setSelectedBodyTypes] = useState(() => {
    const preset = getBodyTypePreset();
    return preset;
  });

  const [selectedVehicleType, setSelectedVehicleType] = useState<DisplayedVehicleType[]>(() => {
    const preset = getVehicleTypePreset(
      selectedCarlineGroups.length > 0 || selectedBodyTypes.length > 0,
    );
    return preset;
  });

  const bodyOrModelFilterActive = useMemo(
    () => selectedBodyTypes.length > 0 || selectedCarlineGroups.length > 0,
    [selectedBodyTypes, selectedCarlineGroups],
  );
  // used for URL preset + "new" group logic only
  const preFilteredCarlineGroups = useMemo(() => {
    const base = filterCarlineGroupsByVehicleType(allCarlineGroups, selectedVehicleType);
    const flattened = flattenCarlineGroups(base); // used only for "new" detection
    return insertNewCarlineGroup(base, flattened);
  }, [selectedVehicleType, allCarlineGroups]);

  const allCarlineGroupsAndNewGroup = useMemo(() => {
    const base = allCarlineGroups;
    const flattened = flattenCarlineGroups(base);
    return insertNewCarlineGroup(base, flattened);
  }, [allCarlineGroups]);

  const flattenedCarlines = useMemo(() => {
    const result = flattenCarlineGroups(allCarlineGroupsAndNewGroup);
    return result;
  }, [allCarlineGroupsAndNewGroup]);

  const carlineGroupsForTags = useMemo(() => {
    const base = allCarlineGroups;
    const flat = flattenCarlineGroups(base);
    return insertNewCarlineGroup(base, flat); // ensures "NEW" exists in tag list too
  }, [allCarlineGroups]);

  const carlinesFilteredByVehicleType = useMemo(() => {
    if (!selectedVehicleType || selectedVehicleType.length === 0) return flattenedCarlines;
    return flattenedCarlines.filter((c) =>
      selectedVehicleType.includes(c.vehicleType as DisplayedVehicleType),
    );
  }, [flattenedCarlines, selectedVehicleType]);

  const carlinesFilteredByCarlineGroup = useMemo(() => {
    return filterByCarlineGroup(carlinesFilteredByVehicleType, selectedCarlineGroups);
  }, [carlinesFilteredByVehicleType, selectedCarlineGroups]);

  const carlinesFilteredByBodyType = useMemo(() => {
    return filterByBodyType(carlinesFilteredByVehicleType, selectedBodyTypes);
  }, [carlinesFilteredByVehicleType, selectedBodyTypes]);

  const filteredCarlines = useMemo(() => {
    // Step 1: Apply the main filtering logic.
    // This produces the "base" result that would normally be displayed.
    const base = filterByBodyType(carlinesFilteredByCarlineGroup, selectedBodyTypes);

    // Step 2: Extract all carlines marked as "new" within the current filtered scope.
    // These are used to simulate a custom "NEW" category — not part of the backend data,
    const newSlice = base.filter((c) => c.markAsNew);

    // Step 3: Merge the two arrays, ensuring "new" carlines appear first.
    // and remove duplicates
    return uniqueByFirst([...newSlice, ...base], (carline) => carline.identifier.id);
  }, [
    carlinesFilteredByBodyType,
    selectedBodyTypes,
    carlinesFilteredByVehicleType,
    selectedCarlineGroups,
  ]);

  const availableVehicleTypes = useMemo(() => {
    if (!bodyOrModelFilterActive) {
      return vehicleTypes;
    }
    const types = Array.from(
      new Set(filteredCarlines.map((carline) => carline.vehicleType).filter(Boolean)),
    ) as DisplayedVehicleType[];
    return types;
  }, [filteredCarlines, bodyOrModelFilterActive]);

  const hasActiveFilters = useMemo(() => {
    const active =
      selectedBodyTypes.length > 0 ||
      selectedCarlineGroups.length > 0 ||
      selectedVehicleType.length > 0;
    return active;
  }, [selectedBodyTypes, selectedCarlineGroups, selectedVehicleType]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedBodyTypes.length > 0) count += selectedBodyTypes.length;
    if (selectedCarlineGroups.length > 0) count += selectedCarlineGroups.length;
    if (selectedVehicleType.length > 0) count += selectedVehicleType.length;
    return count;
  }, [selectedBodyTypes, selectedCarlineGroups, selectedVehicleType]);

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const list: ActiveFilter[] = [];
    selectedBodyTypes.forEach((b) => list.push({ id: b, type: 'bodyType' }));
    selectedCarlineGroups.forEach((g) => {
      list.push({ id: g, type: 'carlineGroup' });
    });
    selectedVehicleType.forEach((v) => {
      list.push({ id: v, type: 'vehicleType' });
    });
    return list;
  }, [selectedBodyTypes, selectedCarlineGroups, selectedVehicleType, preFilteredCarlineGroups]);

  const removeActiveFilter = useCallback(
    (f: ActiveFilter) => {
      if (f.type === 'bodyType') {
        setSelectedBodyTypes((prev) => prev.filter((x) => x !== f.id));
        return;
      }
      if (f.type === 'carlineGroup') {
        setSelectedCarlineGroups((prev) => prev.filter((x) => x !== f.id));
        return;
      }
      setSelectedVehicleType((prev) => prev.filter((x) => x !== f.id));
    },
    [setSelectedBodyTypes, setSelectedCarlineGroups, setSelectedVehicleType],
  );

  const resetAllFilters = () => {
    setSelectedBodyTypes([]);
    setSelectedCarlineGroups([]);
    setSelectedVehicleType([]);
  };

  return {
    selectedBodyTypes,
    setSelectedBodyTypes,
    selectedCarlineGroups,
    setSelectedCarlineGroups,
    selectedVehicleType,
    setSelectedVehicleType,
    hasActiveFilters,
    activeFilterCount,
    activeFilters,
    removeActiveFilter,
    preFilteredCarlineGroups,
    flattenedCarlines,
    carlineGroupsForTags,
    carlinesFilteredByCarlineGroup,
    carlinesFilteredByBodyType,
    filteredCarlines,
    availableVehicleTypes,
    resetAllFilters,
    bodyOrModelFilterActive,
  };
};
